import { useEffect, useMemo, useState } from "react";

const CM_FIELDS = [
  { key: "license_number", label: "License number", required: true },
  { key: "name", label: "Customer / store name", required: true },
  { key: "parent_name", label: "Parent / banner" },
  { key: "channel", label: "Channel" },
  { key: "territory", label: "Territory / region" },
  { key: "website", label: "Website" },
  { key: "city", label: "City / county" },
  { key: "source_row_id", label: "Row ID (optional)" },
];

const WTB_FIELDS = [
  { key: "provider_dispensary_id", label: "Hoodie / provider ID", required: true },
  { key: "provider_dispensary_name", label: "Dispensary name", required: true },
  { key: "banner", label: "Banner" },
  { key: "license_number_raw", label: "License number" },
  { key: "address_line1", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "website", label: "Website" },
  { key: "carried_skus", label: "Carried SKUs" },
  { key: "skus_last_30d", label: "SKUs last 30d" },
  { key: "monthly_sales_bucket", label: "Monthly sales bucket" },
  { key: "sku_count_change_pct", label: "SKU count change %" },
];

const SIMPLE_PATTERNS: Record<string, RegExp[]> = {
  license_number: [/^li?cense/i, /\bca\s*license\b/i, /^lic/i],
  license_number_raw: [/^li?cense/i, /\bca\s*license\b/i, /^lic/i],
  name: [/customer.*name/i, /store.*name/i, /dispensary.*name/i, /^name$/i, /nabis.*customer/i],
  provider_dispensary_name: [/dispensary.*name/i, /store.*name/i, /retailer.*name/i, /^name$/i],
  parent_name: [/parent/i, /\bbanner\b/i, /nabis.*parent/i],
  banner: [/banner/i, /parent/i, /group/i],
  channel: [/\bchannel\b/i],
  territory: [/territory/i, /region/i],
  website: [/website/i, /\burl\b/i, /domain/i],
  city: [/^city$/i, /county/i, /\blocation\b/i],
  state: [/^state$/i, /^st$/i],
  zip: [/zip/i, /postal/i],
  source_row_id: [/^id$/i, /^row.?id$/i, /account.*id/i],
  provider_dispensary_id: [/hoodie.*id/i, /hp_?dispensary.?id/i, /provider.*id/i, /dispensary.?id/i, /^id$/i],
  address_line1: [/address/i, /street/i],
  carried_skus: [/carried.*sku/i, /products.*carried/i],
  skus_last_30d: [/sku.*count/i, /sku.*last/i, /sku.*30/i],
  monthly_sales_bucket: [/monthly.*sale/i, /total.*monthly/i],
  sku_count_change_pct: [/sku.*change/i, /change.*sku/i],
};

function autoMap(headers: string[], kind: "cm" | "wtb"): Record<string, string> {
  const fields = kind === "cm" ? CM_FIELDS : WTB_FIELDS;
  const mapping: Record<string, string> = {};
  const used = new Set<string>();
  for (const f of fields) {
    const ps = SIMPLE_PATTERNS[f.key];
    if (!ps) continue;
    for (const h of headers) {
      if (used.has(h)) continue;
      if (ps.some((p) => p.test(h.trim()))) { mapping[f.key] = h; used.add(h); break; }
    }
  }
  return mapping;
}

interface Props {
  kind: "cm" | "wtb";
  headers: string[];
  initialMapping?: Record<string, string>;
  onChange(mapping: Record<string, string>): void;
}

export default function ColumnMapper({ kind, headers, initialMapping, onChange }: Props) {
  const fields = kind === "cm" ? CM_FIELDS : WTB_FIELDS;
  const [mapping, setMapping] = useState<Record<string, string>>(() => initialMapping ?? autoMap(headers, kind));

  useEffect(() => { onChange(mapping); }, [mapping, onChange]);

  const autoDetected = useMemo(() => autoMap(headers, kind), [headers, kind]);
  const missing = fields.filter((f) => f.required && !mapping[f.key]).map((f) => f.label);

  return (
    <div className="card p-4">
      <h3 className="font-semibold mb-2">Column mapping</h3>
      <p className="text-sm text-slate-500 mb-3">
        We auto-detected {Object.keys(autoDetected).length} of {fields.length} columns. Override any row below.
        {missing.length > 0 && (
          <span className="text-red-600"> Missing required: {missing.join(", ")}.</span>
        )}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-1 font-medium">Target field</th>
            <th className="py-1 font-medium">CSV column</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.key} className="border-t border-slate-100">
              <td className="py-1.5 pr-3 w-1/2">
                {f.label}{f.required && <span className="text-red-600 ml-1">*</span>}
              </td>
              <td className="py-1.5">
                <select
                  className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
                  value={mapping[f.key] || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMapping((m) => {
                      const next = { ...m };
                      if (!v) delete next[f.key];
                      else next[f.key] = v;
                      return next;
                    });
                  }}
                >
                  <option value="">— none —</option>
                  {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
